// Which graphic models can actually be used right now (v0.1.119).
//
// content-service polls ai-gateway for per-PROVIDER health and exposes the
// result per ENGINE at GET /v1/image-engines/health. When a provider's
// account is cut off — fal.ai's balance ran out on 2026-08-07 and every
// call 403s — the engines that spend it come back `available:false`.
// Without this, the operator picks Nano Banana 2, waits, and silently gets
// a roster fallback (or nothing) with no explanation anywhere.
//
// Failure policy: AVAILABLE. If the endpoint 404s (older gateway), errors,
// or hasn't answered yet, `unavailable` is empty and every option renders
// exactly as it did before this hook existed. A false "under maintenance"
// label would block a working engine, which is strictly worse than missing
// a broken one — the server-side roster fallback still covers that case.

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export type EngineHealth = {
  /** Internal engine id, e.g. "fal-nano-banana-2-edit". */
  engine: string;
  /** Dropdown key from image-options.ts, e.g. "nano-banana-2". */
  ui_id?: string;
  provider: string;
  available: boolean;
  /** "maintenance" when unavailable. */
  reason?: string;
  /** The provider's own wording, e.g. "exhausted balance". */
  detail?: string;
};

export type EngineHealthState = {
  /** Dropdown keys that must be disabled. Empty until proven otherwise. */
  unavailable: Set<string>;
  /** ui_id → provider detail, for the inline explanation. */
  details: Map<string, string>;
  loaded: boolean;
};

export function useEngineHealth(): EngineHealthState {
  const { data, isSuccess } = useQuery({
    queryKey: ["image-engines", "health"],
    queryFn: () => api<EngineHealth[]>("/image-engines/health"),
    // Maintenance mode changes on the order of hours, and the server side
    // only re-polls every 5 minutes, so there is nothing to gain from a
    // tighter refresh.
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: false,
    // One retry, then give up quietly and let everything render available.
    retry: 1,
  });

  return useMemo(() => {
    const unavailable = new Set<string>();
    const details = new Map<string, string>();
    for (const e of data ?? []) {
      if (!e.ui_id || e.available) continue;
      unavailable.add(e.ui_id);
      if (e.detail) details.set(e.ui_id, e.detail);
    }
    return { unavailable, details, loaded: isSuccess };
  }, [data, isSuccess]);
}

/**
 * Label suffix for a disabled option. Kept here so Generate and Bulk can't
 * drift apart.
 */
export function maintenanceSuffix(tr: boolean): string {
  return tr ? " — bakımda" : " — under maintenance";
}
